import React, {useState, useEffect, useCallback} from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import Select from 'react-select';

function AllFarmPondSensorData() {
    const [companies, setCompanies] = useState([]);
    const [farms, setFarms] = useState([]);
    const [companyFilter, setCompanyFilter] = useState('');
    const [farmFilter, setFarmFilter] = useState('');
    const [sensorData, setSensorData] = useState([]);
    const [ngos, setNgos] = useState([]);
    const [filterNgo, setFilterNgo] = useState('');
    const [sensorProblem, setSensorProblem] = useState('');
    const [sort, setSort] = useState('asc');
    const [show_offline, set_show_offline] = useState('');
    const [pageSize] = useState(10);
    const [pagination, setPagination] = useState({
        total: 0,
        currentPage: 1,
        totalPages: 1,
    });



    const fetchCompanies = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/companies`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setCompanies(response.data);
        } catch (error) {
            Swal.fire('Error', 'Failed to fetch companies.', 'error');
        }
    };

    const fetchFarms = async (companyId) => {
        const token = localStorage.getItem('token');
        try {
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/farm/company/${companyId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setFarms(response.data);
        } catch (error) {
            Swal.fire('Error', 'Failed to fetch farms.', 'error');
        }
    };

    const fetchSensorData = useCallback(async (page = 1) => {
        const token = localStorage.getItem('token');
        try {
            const params = { page, limit: pageSize };
            if (companyFilter) params.company_id = companyFilter;
            if (farmFilter) params.farm_id = farmFilter;
            if (filterNgo) params.ngo_id = filterNgo;
            if(show_offline) params.show_offline = show_offline;
            if(sensorProblem) params.sensor_problem = sensorProblem;
            params.sort = sort;

            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/pond/all/farms/sensor-data`, {
                headers: { Authorization: `Bearer ${token}` },
                params,
            });

            setSensorData(response.data.data);
            setPagination({
                total: response.data.total,
                currentPage: response.data.page,
                totalPages: response.data.totalPages,
            });
        } catch (error) {
            Swal.fire('Error', 'Failed to fetch sensor data.', 'error');
        }
    }, [companyFilter, farmFilter,filterNgo,show_offline,sort,sensorProblem, pageSize]);

    const fetchNgos = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/ngos`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setNgos(res.data);
        } catch (err) {
            console.error('Failed to fetch NGOs', err);
        }
    };

    useEffect(() => {
        fetchCompanies();
        fetchSensorData();
        fetchNgos();
    }, [fetchSensorData]);


    const handleFilterSelectChange = (selectedOption, name) => {
        if (name === 'company') {
            const value = selectedOption ? selectedOption.value : '';
            setCompanyFilter(value);
            setFarmFilter('');
            setFarms([]);
            if (value) fetchFarms(value);
        } else if (name === 'farm') {
            setFarmFilter(selectedOption ? selectedOption.value : '');
        }
    };

    const handlePageChange = (pageNumber) => {
        if (pageNumber > 0 && pageNumber <= pagination.totalPages) {
            fetchSensorData(pageNumber);
        }
    };

    const getPaginationButtons = () => {
        const { currentPage, totalPages } = pagination;
        const pageNumbers = [];

        let startPage = Math.max(currentPage - 2, 1);
        let endPage = Math.min(currentPage + 2, totalPages);

        if (startPage > 1) {
            pageNumbers.push(1);
            if (startPage > 2) pageNumbers.push('...');
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        if (endPage < totalPages - 1) pageNumbers.push('...');
        if (endPage < totalPages) pageNumbers.push(totalPages);

        return pageNumbers;
    };

    return (
        <div className="container mx-auto p-6">
            <h1 className="text-3xl font-bold mb-6">Farm Overview</h1>

            <div className="bg-white shadow-md rounded-lg p-6 mb-6">
                <h2 className="text-2xl font-semibold mb-4">Filter Farm</h2>
                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className="block text-gray-700">Select Company</label>
                        <Select
                            options={[{ value: '', label: 'Select Company' }, ...companies.map(c => ({ value: c._id, label: c.name }))]}
                            onChange={(option) => handleFilterSelectChange(option, 'company')}
                            value={companyFilter ? { value: companyFilter, label: companies.find(c => c._id === companyFilter)?.name } : { value: '', label: 'Select Company' }}
                        />
                    </div>
                    <div>
                        <label className="block text-gray-700">Select Farm</label>
                        <Select
                            options={[{ value: '', label: 'Select Farm' }, ...farms.map(f => ({ value: f._id, label: f.name }))]}
                            onChange={(option) => handleFilterSelectChange(option, 'farm')}
                            value={farmFilter ? { value: farmFilter, label: farms.find(f => f._id === farmFilter)?.name } : { value: '', label: 'Select Farm' }}
                            isDisabled={!companyFilter}
                        />
                    </div>
                    <div className="mb-8">
                        <label className="block text-gray-700 mb-2">
                            Filter by NGO
                        </label>
                        <select
                            value={filterNgo}
                            onChange={(e) => setFilterNgo(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        >
                            <option value="">All NGOs</option>
                            {ngos.map((n) => (
                                <option key={n._id} value={n._id}>{n.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="mb-8">
                        <label className="block text-gray-700 mb-2">
                            Problematic Sensors
                        </label>
                        <select
                            value={sensorProblem}
                            onChange={(e) => setSensorProblem(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        >
                            <option value="">All</option>
                            <option value="show">Show</option>
                            <option value="hide">Hide</option>

                        </select>
                    </div>
                    <div className="">
                        <div className="flex justify-around w-full">
                            <button
                                className={`font-bold py-2 px-4 rounded  ${show_offline === 'show' ? 'bg-blue-500 hover:bg-blue-700 text-white' : 'bg-gray-500 hover:bg-gray-700 text-white'}`}
                                onClick={() => set_show_offline(show_offline === 'show' ? '' : 'show')}
                            >
                                {show_offline === 'show' ? 'Show All' : 'Show Offline'}
                            </button>
                        </div>

                    </div>
                    <div className="">
                        <div className="flex justify-around w-full">
                            <button
                                className="bg-blue-500 hover:bg-blue-700 text-white font-bold  py-2 px-4 rounded"
                                onClick={() => setSort(sort === 'asc' ? 'desc' : 'asc')}
                            >
                                {sort === 'asc' ? 'Ascending' : 'Descending'}

                            </button>
                        </div>

                    </div>

                </div>


            </div>

            <h1 className="text-3xl font-bold mb-6">{show_offline === 'show' ? 'Offline Farm Pond Sensor Data.':'All Farm Pond Sensor Data.'} {sort === 'asc' ? 'Ascending' : 'Descending'}</h1>

            {sensorData.length > 0 ? (
                <table className="min-w-full bg-white">
                    <thead>
                    <tr>
                        <th className="py-2 px-4 border-b">Farm Name</th>
                        <th className="py-2 px-4 border-b">Phone</th>
                        <th className="py-2 px-4 border-b">Location</th>
                        <th className="py-2 px-4 border-b">Pond Name</th>
                        <th className="py-2 px-4 border-b">Device Serial</th>
                        <th className="py-2 px-4 border-b">Sensors</th>
                        <th className="py-2 px-4 border-b">Last Updated</th>
                    </tr>
                    </thead>
                    <tbody>
                    {sensorData.map((item, index) => (
                        <tr key={index}>
                            <td className="py-2 px-4 border-b text-center">{item.farm_name}</td>
                            <td className="py-2 px-4 border-b text-center">{item.phone}</td>
                            <td className="py-2 px-4 border-b text-center">{item.farm_location}</td>
                            <td className="py-2 px-4 border-b text-center">{item.pond_name}</td>
                            <td className="py-2 px-4 border-b text-center">
                                <div className="bg-yellow-100 text-yellow-800 font-semibold py-2 px-4 border-b text-center rounded ">
                                    {item.device_serial}
                                </div>
                            </td>

                            <td className="py-2 px-4 border-b text-left align-top">
                                <div className="space-y-2">
                                    {item.sensors.map((sensor, idx) => (
                                        <div key={idx} className={`flex justify-between items-center ${sensor.sensor_problem ? 'bg-red-100' : 'bg-green-100'} p-2 rounded-lg shadow-sm`}>
                                            <span className="text-gray-800 font-medium">{sensor.sensor_name}</span>
                                            <span className="text-right">
                                            <span className="text-gray-900 font-semibold">{sensor.value}</span>
                                            <span className="ml-2 inline-block bg-blue-200 text-blue-800 text-xs font-bold px-2 py-0.5 rounded">
                                                {sensor.unit}
                                            </span>
                                        </span>
                                        </div>
                                    ))}
                                </div>
                            </td>

                            <td className="py-2 px-4 border-b text-center">
                                <div className="inline-block bg-red-100 text-red-700 font-medium px-3 py-2 rounded shadow-sm">
                                    {item.last_updated}
                                </div>
                            </td>

                        </tr>
                    ))}
                    </tbody>
                </table>
            ) : (
                <p>No sensor data found.</p>
            )}

            {pagination.total > 0 && (
                <div className="mt-6 flex justify-center items-center space-x-2">
                    <button onClick={() => handlePageChange(1)} disabled={pagination.currentPage === 1} className="px-3 py-1 rounded bg-gray-200">First</button>
                    <button onClick={() => handlePageChange(pagination.currentPage - 1)} disabled={pagination.currentPage === 1} className="px-3 py-1 rounded bg-gray-200">Previous</button>
                    {getPaginationButtons().map((page, index) => (
                        page === '...'
                            ? <span key={index} className="px-3 py-1">...</span>
                            : <button key={index} onClick={() => handlePageChange(page)} className={`px-3 py-1 rounded ${pagination.currentPage === page ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-800'}`}>{page}</button>
                    ))}
                    <button onClick={() => handlePageChange(pagination.currentPage + 1)} disabled={pagination.currentPage === pagination.totalPages} className="px-3 py-1 rounded bg-gray-200">Next</button>
                    <button onClick={() => handlePageChange(pagination.totalPages)} disabled={pagination.currentPage === pagination.totalPages} className="px-3 py-1 rounded bg-gray-200">Last</button>
                </div>
            )}
        </div>
    );
}

export default AllFarmPondSensorData;
